import * as React from "react";
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Select,
  MenuItem,
  InputAdornment,
  Button,
  Divider,
  FormControl,
  InputLabel,
  Avatar,
  FormHelperText,
  CircularProgress,
} from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import LanguageIcon from "@mui/icons-material/Language";
import InsertInvitationOutlinedIcon from "@mui/icons-material/InsertInvitationOutlined";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import {
  colors,
  typography,
  spacing,
} from "../../assets/styles/tokens";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import {
  selectAllUsers,
  selectUserDetails,
  selectUserWebsitePermissions,
} from "../../features/userSlice";
import { useSelector } from "react-redux";
import {
  validateActionItemName,
  validateDateFormat,
  validateText,
  isEmpty,
} from "../../utils/actionItemsValidation";
import { useLocation, useSearchParams } from "react-router-dom";
import { useGenericBusinessLogic } from "../../utils/useGenericBusinessLogic";
import { capitalizeEachWords } from "../../utils/helpers";

export interface SharedTaskProps {
  assignee: { id: string; name: string };
  due_date: Dayjs | string | null;
  priority: string;
  website: { id: string; name: string };
}
interface Task {
  itemId: number;
  task_name: string;
  category: string;
  target: string;
  website: string;
  due_date: Dayjs | null;
  assignee: string;
  priority: string;
  referTo: string;
}

interface AddTasksProps {
  sharedProps?: SharedTaskProps;
  tasks: Task[]; // ← add
  setTasks: React.Dispatch<React.SetStateAction<Task[]>>;
  onBack: () => void;
  onSave: () => void;
  saveTrackerDataId?: string;
}

const defaultTask = (id: number): Task => ({
  itemId: id,
  task_name: "",
  category: "",
  target: "",
  website: "",
  due_date: null,
  assignee: "",
  priority: "",
  referTo: "",
});

export default function AddTasks({
  sharedProps,
  tasks,
  setTasks,
  onBack,
  onSave,
  saveTrackerDataId,
}: AddTasksProps) {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  //const [tasks, setTasks] = React.useState<Task[]>([defaultTask(1)]);
  const [formError, setFormError] = React.useState<any>({});
  const [pickerOpen, setPickerOpen] = React.useState<number | null>(null);
  const allUsers = useSelector(selectAllUsers);
  const websitePermissions = useSelector(selectUserWebsitePermissions) as any;
  const owner = useSelector(selectUserDetails) as any;

  const websiteSharedUid = searchParams.get("workspace");

  const task_structure_shared_uid = "484362c5f00c4866ad1b4562b47aa27b";
  let currentPath = window.location.origin + location.pathname;
  const segment_type = "Website";

  const { createTasks, loading } = useGenericBusinessLogic();
  const sharedPropsApplied = React.useRef(false);

  // React.useEffect(() => {
  //   if (tasks.length === 0) {
  //     setTasks([defaultTask(1)]);
  //   }
  //   // eslint-disable-next-line
  // }, []);

  const hasShared =
    sharedProps &&
    (sharedProps.assignee ||
      sharedProps.due_date ||
      sharedProps.priority ||
      sharedProps.website);

  const siteOptions =
    websitePermissions?.map((siteObj: any) => {
      const uid = Object.keys(siteObj)[0]; // UID of the site
      const siteName = siteObj[uid].ui_site_name; // Site name
      const dimensions = siteObj[uid]?.dimensions; // Site dimensions
      return { uid, siteName, dimensions }; // Return as an object with uid and siteName
    }) || [];

  const updateTask = (
    itemId: number,
    field: keyof Task,
    value: string | Dayjs | null,
  ) => {
    setTasks((prev) =>
      prev.map((item) =>
        item.itemId === itemId ? { ...item, [field]: value } : item,
      ),
    );

    const validationRules: Record<string, (val: any) => string> = {
      task_name: validateActionItemName,
      category: validateText,
      target: validateText,
      description: validateText,
      due_date: validateDateFormat,
      assignee: isEmpty,
      priority: isEmpty,
      // referTo: isEmpty,
    };

    const errorMsg = validationRules[field]?.(value) || "";

    setFormError((prevErrors: any) => {
      const newErrors = { ...prevErrors };

      // Ensure the object for the specific `itemId` exists
      if (!newErrors[itemId]) {
        newErrors[itemId] = {};
      }

      // Set or remove field error for the specific itemId
      if (errorMsg) {
        newErrors[itemId][field] = errorMsg;
      } else {
        delete newErrors[itemId][field];
      }
      return newErrors;
    });
  };

  const addAnother = () => {
    setTasks((prev) => {
      const newTask: any = {
        ...defaultTask(prev.length + 1),
        // Only pre-fill sharedProps on the NEW task
        ...(hasShared && {
          assignee: sharedProps?.assignee?.id ?? "",
          due_date: sharedProps?.due_date ?? null,
          priority: sharedProps?.priority ?? "",
          website: sharedProps?.website?.id ?? "",
        }),
        ...(owner && { owner: owner?.user_id }),
      };
      return [...prev, newTask];
    });
  };

  const deleteTask = (itemId: number) => {
    setTasks((prevData) => {
      const newData = prevData.filter((data) => data.itemId !== itemId); // Remove item
      // Re-sequence itemId
      return newData.map((item, index) => ({
        ...item,
        itemId: index + 1, // Recalculate itemId based on index
      }));
    });
  };

  const isFormValid = (): boolean => {
    return tasks.every((task) => {
      // task_name is always required
      if (!task.task_name?.trim() || task.task_name.length < 8) return false;
      if (!task.category?.trim() || task.category.length < 4) return false;
      if (!task.target?.trim() || task.target.length < 4) return false;

      // Only validate if NOT covered by sharedProps
      if (!sharedProps?.assignee?.id && !task.assignee) return false;
      if (!sharedProps?.due_date && !task.due_date) return false;
      if (!sharedProps?.priority && !task.priority) return false;
      //if (!sharedProps?.website?.id && !task.website) return false;

      return true;
    });
  };

  // On initial load, pre-fill fields with sharedProps
  React.useEffect(() => {
    if (sharedPropsApplied.current) return; // ← skip if already ran once
    sharedPropsApplied.current = true;

    if (hasShared && sharedProps && Object.values(sharedProps).length > 0) {
      setTasks((prevState: any[]) =>
        prevState.map((item) => ({
          ...item,
          // only fill if the field is currently empty — never overwrite user edits
          assignee: item.assignee || sharedProps?.assignee?.id || "",
          due_date: item.due_date || sharedProps?.due_date || null,
          priority: item.priority || sharedProps?.priority || "",
          website: item.website || sharedProps?.website?.id || "",
        })),
      );
    }
    if (owner) {
      setTasks((prevState: any[]) =>
        prevState.map((item) => ({
          ...item,
          owner: item.owner || owner?.user_id,
        })),
      );
    }
    // eslint-disable-next-line
  }, []);

  const multiTaskRequestBody = () => {
    return {
      tasks: tasks?.map((item: any) => ({
        task_structure_shared_uid: task_structure_shared_uid,
        owner: item?.owner,
        assignee: sharedProps?.assignee?.id
          ? sharedProps?.assignee?.id
          : item?.assignee,
        reference_id: sharedProps?.website?.id
          ? sharedProps?.website?.id
          : item?.website || websiteSharedUid,
        due_date: sharedProps?.due_date
          ? sharedProps?.due_date
          : item?.due_date,
        segment_type: segment_type?.toLowerCase(),
        segment_value: sharedProps?.website?.id
          ? sharedProps?.website?.id
          : item?.website || websiteSharedUid,
        website_shared_uid: sharedProps?.website?.id
          ? sharedProps?.website?.id
          : item?.website || websiteSharedUid,
        reference_url: `${currentPath}/edit_action_item?selectedSite=${
          sharedProps?.website?.id
            ? sharedProps?.website?.id
            : item?.website || websiteSharedUid
        }`,
        data: {
          category: item?.category,
          target: item?.target,
          priority: sharedProps?.priority
            ? capitalizeEachWords(sharedProps?.priority)
            : capitalizeEachWords(item?.priority ?? ""),
          action_item: item?.task_name,
        },
      })),
    };
  };

  // Call API to create tasks and close modal on success
  const handleCreateNewTasks = async () => {
    if (!isFormValid() || tasks?.length === 0) return;
    const requestBody = multiTaskRequestBody();
    createTasks(requestBody, onSave);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            py: spacing.Spacing.xl.$value,
            px: spacing.Spacing.xl.$value,
          }}
        >
          <IconButton disabled={loading} onClick={onBack} size="small">
            <ArrowBackIosIcon
              fontSize="small"
              sx={{ color: colors.GrayDarkMode[700].$value }}
            />
          </IconButton>
          <Typography
            sx={{
              fontWeight: typography.Font_weight.Medium.$value,
              fontSize: typography.Size.Text.xl.$value,
              color: colors.GrayLightMode[900].$value,
            }}
          >
            Add Tasks
          </Typography>
          <Button
            data-tracker={saveTrackerDataId}
            disabled={!isFormValid() || loading || tasks?.length === 0}
            onClick={handleCreateNewTasks}
            startIcon={
              loading ? (
                <CircularProgress color="secondary" size={14} />
              ) : undefined
            }
            sx={{
              color: colors.Brand[600].$value,
              textTransform: "none",
              fontWeight: typography.Font_weight.Bold.$value,
              padding: spacing.Spacing.none.$value,
              "&.Mui-disabled": {
                color: colors.GrayLightMode[400].$value,
              },
            }}
          >
            {loading ? "Saving..." : "Save"}
          </Button>
        </Box>
        <Divider />

        {/* Scrollable body */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            px: spacing.Spacing.xl.$value,
            pt: spacing.Spacing.md.$value,
          }}
        >
          {/* ── Shared Defaults Banner ── */}
          {hasShared && (
            <Box
              sx={{
                border: `1px solid ${colors.GrayLightMode[200].$value}`,
                borderRadius: spacing.Radius.md.$value,
                p: spacing.Spacing.md.$value,
                mb: spacing.Spacing.xl.$value,
                backgroundColor: colors.Base.White.$value,
              }}
            >
              {/* Banner header */}
              <Typography
                sx={{
                  fontSize: typography.Size.Text.md.$value,
                  fontWeight: typography.Font_weight.Medium.$value,
                  color: colors.GrayDarkMode[700].$value,
                  marginInline: spacing.Spacing.md.$value,
                  marginBottom: spacing.Spacing.md.$value,
                }}
              >
                Shared Defaults
              </Typography>
              <Divider></Divider>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "flex-end",
                  marginTop: spacing.Spacing.md.$value,
                  marginInline: spacing.Spacing.md.$value,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: spacing.Spacing.md.$value,
                  }}
                >
                  {/* Assignee row */}
                  {sharedProps?.assignee?.name && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: spacing.Spacing.xs.$value,
                      }}
                    >
                      <AccountCircleIcon
                        fontSize="medium"
                        sx={{ color: colors.GrayDarkMode[700].$value }}
                      />
                      <Typography
                        sx={{
                          fontSize: typography.Size.Text.xs.$value,
                          color: colors.GrayDarkMode[700].$value,
                          fontWeight: typography.Font_weight.Regular.$value,
                        }}
                      >
                        {sharedProps?.assignee?.name}
                      </Typography>
                    </Box>
                  )}

                  {/* Due Date row */}
                  {sharedProps?.due_date && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: spacing.Spacing.xs.$value,
                      }}
                    >
                      <InsertInvitationOutlinedIcon
                        fontSize="medium"
                        sx={{
                          color: colors.GrayDarkMode[700].$value,
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: typography.Size.Text.xs.$value,
                          color: colors.GrayDarkMode[700].$value,
                          fontWeight: typography.Font_weight.Regular.$value,
                        }}
                      >
                        {dayjs(sharedProps?.due_date).format("DD/MM/YYYY")}
                      </Typography>
                    </Box>
                  )}

                  {/* Priority row */}
                  {sharedProps?.priority &&
                    (() => {
                      const priorityIconMap: Record<string, React.ReactNode> = {
                        high: (
                          <ExpandLessIcon
                            fontSize="medium"
                            sx={{ color: colors.Error[500].$value }}
                          />
                        ),
                        medium: (
                          <DragHandleIcon
                            fontSize="medium"
                            sx={{ color: colors.Warning[500].$value }}
                          />
                        ),
                        low: (
                          <ExpandMoreIcon
                            fontSize="medium"
                            sx={{ color: colors.Blue[500].$value }}
                          />
                        ),
                        highest: (
                          <KeyboardDoubleArrowUpIcon
                            fontSize="medium"
                            sx={{ color: colors.Error[500].$value }}
                          />
                        ),
                        lowest: (
                          <KeyboardDoubleArrowDownIcon
                            fontSize="medium"
                            sx={{ color: colors.Blue[500].$value }}
                          />
                        ),
                      };
                      return (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: spacing.Spacing.xs.$value,
                          }}
                        >
                          {priorityIconMap[sharedProps?.priority] ?? null}
                          <Typography
                            sx={{
                              fontSize: typography.Size.Text.xs.$value,
                              color: colors.GrayDarkMode[700].$value,
                              textTransform: "capitalize",
                            }}
                          >
                            {sharedProps?.priority}
                          </Typography>
                        </Box>
                      );
                    })()}

                  {/* Website row */}
                  {sharedProps?.website?.name && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: spacing.Spacing.xs.$value,
                      }}
                    >
                      <LanguageIcon
                        fontSize="medium"
                        sx={{ color: colors.GrayLightMode[700].$value }}
                      />
                      <Typography
                        sx={{
                          fontSize: typography.Size.Text.xs.$value,
                          color: colors.GrayLightMode[700].$value,
                        }}
                      >
                        {sharedProps?.website.name}
                      </Typography>
                    </Box>
                  )}
                </Box>
                {/* Edit → go back to shared step */}
                <IconButton
                  size="small"
                  onClick={onBack}
                  sx={{ p: spacing.Spacing.lg.$value }}
                >
                  <EditIcon
                    sx={{
                      fontSize: typography.Size.Text.xxl.$value,
                      color: colors.GrayDarkMode[700].$value,
                    }}
                  />
                </IconButton>
              </Box>
            </Box>
          )}

          {/* ── Task Cards ── */}
          {tasks.map((task, index) => (
            <Box
              key={task.itemId}
              sx={{
                border: `1px solid ${colors.GrayLightMode[200].$value}`,
                borderRadius: spacing.Radius.md.$value,
                p: spacing.Spacing.md.$value,
                mb: spacing.Spacing.md.$value,
                backgroundColor: colors.Base.White.$value,
              }}
            >
              {/* Task header with close button */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography
                  sx={{
                    fontSize: typography.Size.Text.xs.$value,
                    fontWeight: typography.Font_weight.Medium.$value,
                    color: colors.GrayLightMode[500].$value,
                  }}
                >
                  Task #{index + 1}
                </Typography>

                {task?.itemId > 1 && (
                  <IconButton
                    size="small"
                    onClick={() => deleteTask(task.itemId)}
                  >
                    <CloseIcon
                      fontSize="medium"
                      sx={{ color: colors.Error[600].$value }}
                    />
                  </IconButton>
                )}
              </Box>

              {/* Task Name */}
              <TextField
                disabled={loading}
                fullWidth
                placeholder="Task Name"
                value={task.task_name}
                onChange={(e) =>
                  updateTask(task.itemId, "task_name", e.target.value)
                }
                error={formError[task.itemId]?.task_name}
                helperText={formError[task.itemId]?.task_name}
                variant="standard"
                sx={{
                  mb: spacing.Spacing.xl.$value,
                  "& .MuiInput-underline:before": {
                    borderBottomColor: colors.GrayDarkMode[300].$value,
                  },
                  "& .MuiInput-underline:hover:before": {
                    borderBottomColor: colors.Brand[600].$value,
                  },
                  "& .MuiInput-underline:after": {
                    borderBottomColor: colors.Brand[600].$value,
                  },
                  "& .MuiInputBase-input::placeholder": {
                    color: colors.GrayDarkMode[400].$value,
                    opacity: 1,
                  },
                }}
                color="secondary"
              />

              {/* Category */}
              <TextField
                disabled={loading}
                fullWidth
                placeholder="e.g. Deal, Design, Research"
                label="Category"
                value={task.category}
                onChange={(e) =>
                  updateTask(task.itemId, "category", e.target.value)
                }
                error={formError[task.itemId]?.category}
                helperText={formError[task.itemId]?.category}
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
                variant="outlined"
                size="small"
                sx={{
                  mb: spacing.Spacing["3xl"].$value,
                  "& .MuiOutlinedInput-root": {
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: colors.Brand[600].$value,
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: colors.Brand[600].$value,
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: colors.Brand[600].$value,
                  },
                }}
              />

              {/* Target */}
              <TextField
                disabled={loading}
                fullWidth
                placeholder="Specify your target for this item"
                label="Target"
                value={task.target}
                onChange={(e) =>
                  updateTask(task.itemId, "target", e.target.value)
                }
                error={formError[task.itemId]?.target}
                helperText={formError[task.itemId]?.target}
                variant="outlined"
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: colors.Brand[600].$value,
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: colors.Brand[600].$value,
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: colors.Brand[600].$value,
                  },
                }}
                size="small"
                multiline
              />

              {/* Website — only shown if NOT already set via shared props */}
              {!sharedProps?.website?.id && (
                <FormControl
                  error={!!formError[task.itemId]?.website}
                  fullWidth
                  sx={{
                    mt: spacing.Spacing["3xl"].$value,
                    mb: spacing.Spacing.xs.$value,
                  }}
                >
                  <InputLabel
                    shrink
                    sx={{
                      color: colors.GrayLightMode[500].$value,
                      background: colors.Base.White.$value,
                      paddingRight: spacing.Spacing.xs.$value,
                      "&.Mui-focused": {
                        color: colors.Brand[600].$value,
                      },
                    }}
                  >
                    Website (optional)
                  </InputLabel>
                  <Select
                    disabled={loading}
                    value={task.website}
                    onChange={(e) =>
                      updateTask(task.itemId, "website", e.target.value)
                    }
                    displayEmpty
                    notched
                    label="Website (optional)"
                    size="small"
                    startAdornment={
                      <InputAdornment position="start">
                        <LanguageIcon
                          fontSize="medium"
                          sx={{
                            color: loading
                              ? colors.GrayDarkMode[400].$value
                              : colors.GrayDarkMode[700].$value,
                          }}
                        />
                      </InputAdornment>
                    }
                    // renderValue={(selected) =>
                    //   selected ? (
                    //     <Box
                    //       sx={{
                    //         display: "flex",
                    //         alignItems: "center",
                    //         gap: spacing.Spacing.xs.$value,
                    //       }}
                    //     >
                    //       <Typography
                    //         sx={{
                    //           fontSize: typography.Size.Text.sm.$value,
                    //           color: colors.GrayLightMode[900].$value,
                    //         }}
                    //       >
                    //         {selected as string}
                    //       </Typography>
                    //     </Box>
                    //   ) : (
                    //     <Box
                    //       sx={{
                    //         display: "flex",
                    //         alignItems: "center",
                    //         gap: spacing.Spacing.md.$value,
                    //       }}
                    //     >
                    //       <LanguageIcon
                    //         fontSize="small"
                    //         sx={{ color: colors.GrayDarkMode[700].$value }}
                    //       />
                    //       <Typography
                    //         sx={{
                    //           fontSize: typography.Size.Text.sm.$value,
                    //           color: colors.GrayDarkMode[500].$value,
                    //         }}
                    //       >
                    //         Select Website
                    //       </Typography>
                    //     </Box>
                    //   )
                    // }
                    sx={{
                      borderRadius: spacing.Radius.xs.$value,
                      fontSize: typography.Size.Text.sm.$value,
                      "& .MuiSelect-select": {
                        paddingTop: spacing.Spacing.md1.$value,
                        paddingBottom: spacing.Spacing.md1.$value,
                        color: colors.GrayLightMode[900].$value,
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: colors.Brand[600].$value,
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: colors.Brand[600].$value,
                      },
                    }}
                  >
                    <MenuItem value="">Select Website</MenuItem>
                    {siteOptions?.map((site: any) => (
                      <MenuItem key={site.uid} value={site.uid}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: spacing.Spacing.md.$value,
                          }}
                        >
                          {site.siteName}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                  {formError[task.itemId]?.website && (
                    <FormHelperText>
                      {formError[task.itemId].website}
                    </FormHelperText>
                  )}
                </FormControl>
              )}

              {/* Due Date + Priority + Assignee row — only shown if NOT set via shared props */}
              {(!sharedProps?.due_date ||
                !sharedProps?.priority ||
                !sharedProps?.assignee?.id) && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: spacing.Spacing.md.$value,
                    marginBlock: `${spacing.Spacing["3xl1"].$value} ${spacing.Spacing.xs.$value}`,
                    paddingInline: spacing.Spacing.lg1.$value,
                  }}
                >
                  {/* Due Date — hide if shared */}
                  {!sharedProps?.due_date && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: spacing.Spacing.md.$value,
                        flex: 1,
                      }}
                    >
                      <DatePicker
                        disabled={loading}
                        value={task.due_date ? dayjs(task.due_date) : null}
                        minDate={dayjs()}
                        disablePast
                        format="DD/MM/YYYY"
                        onChange={(date) => {
                          if (date)
                            updateTask(
                              task.itemId,
                              "due_date",
                              dayjs(date).format("YYYY-MM-DD"),
                            );
                        }}
                        // FIX: keep controlled open state (required — can't mix controlled/uncontrolled)
                        open={pickerOpen === index}
                        onOpen={() => setPickerOpen(index)}
                        onClose={() => setPickerOpen(null)}
                        // This stops MUI from activating the DD/MM/YYYY format mask on focus
                        //closeOnSelect
                        slots={{
                          openPickerIcon: InsertInvitationOutlinedIcon,
                        }}
                        slotProps={{
                          textField: {
                            variant: "standard",
                            size: "small",
                            placeholder: "Due Date",
                            disabled: loading,
                            error: !!formError[task.itemId]?.due_date,
                            helperText: formError[task.itemId]?.due_date || "",
                            style: { textTransform: "lowercase" },
                            InputProps: {
                              startAdornment: (
                                <InputAdornment position="start">
                                  <InsertInvitationOutlinedIcon
                                    fontSize="medium"
                                    sx={{
                                      color: loading
                                        ? colors.GrayDarkMode[300].$value // dimmed when disabled
                                        : colors.GrayDarkMode[700].$value,
                                      cursor: loading
                                        ? "not-allowed"
                                        : "pointer",
                                      pointerEvents: loading ? "none" : "auto", // blocks the click entirely
                                    }}
                                    onClick={(e) => {
                                      if (loading) return;
                                      e.stopPropagation();
                                      setPickerOpen(index);
                                    }}
                                  />
                                </InputAdornment>
                              ),
                              disableUnderline: true,
                              endAdornment: null,
                              //  inputComponent renders a plain div instead of an <input>
                              // so MUI never activates the format mask at all
                              inputComponent: "div" as any,
                              inputProps: {
                                style: { display: "none" }, // hide the div, placeholder shows instead
                              },
                            },
                            //  render placeholder manually since input is hidden
                            // overlay it absolutely so layout is unaffected
                            sx: {
                              flex: 1,
                              position: "relative",
                              "& .MuiInputBase-root": {
                                cursor: "pointer",
                              },
                              "& .MuiInputBase-root::after": {
                                content: task.due_date
                                  ? `"${dayjs(task.due_date).format("DD/MM/YYYY")}"`
                                  : '"Due Date"',
                                position: "absolute",
                                left: 30, // align after the start icon
                                color: task.due_date
                                  ? colors.GrayLightMode[900].$value
                                  : colors.GrayDarkMode[400].$value,
                                fontSize: typography.Size.Text.sm.$value,
                                pointerEvents: "none",
                              },
                            },
                          },
                          openPickerButton: {
                            sx: { display: "none" }, // hide MUI's own button, icon in startAdornment handles it
                          },
                          actionBar: {
                            sx: {
                              "& .MuiButton-root": {
                                color: colors.Brand[700].$value,
                              },
                              "& .MuiButton-root:last-of-type": {
                                color: colors.Brand[600].$value,
                              },
                            },
                          },
                        }}
                      />
                    </Box>
                  )}

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: spacing.Spacing.md.$value,
                    }}
                  >
                    {/* Priority — hide if shared */}
                    {!sharedProps?.priority &&
                      (() => {
                        const priorityConfig: Record<
                          string,
                          { icon: React.ReactNode }
                        > = {
                          high: {
                            icon: (
                              <ExpandLessIcon
                                fontSize="medium"
                                sx={{ color: colors.Error[500].$value }}
                              />
                            ),
                          },
                          medium: {
                            icon: (
                              <DragHandleIcon
                                fontSize="medium"
                                sx={{ color: colors.Warning[500].$value }}
                              />
                            ),
                          },
                          low: {
                            icon: (
                              <ExpandMoreIcon
                                fontSize="medium"
                                sx={{ color: colors.Blue[500].$value }}
                              />
                            ),
                          },
                          highest: {
                            icon: (
                              <KeyboardDoubleArrowUpIcon
                                fontSize="medium"
                                sx={{ color: colors.Error[500].$value }}
                              />
                            ),
                          },
                          lowest: {
                            icon: (
                              <KeyboardDoubleArrowDownIcon
                                fontSize="medium"
                                sx={{ color: colors.Blue[500].$value }}
                              />
                            ),
                          },
                        };

                        return (
                          <FormControl
                            error={!!formError[task.itemId]?.priority}
                          >
                            <Select
                              disabled={loading}
                              value={task.priority}
                              onChange={(e) =>
                                updateTask(
                                  task.itemId,
                                  "priority",
                                  e.target.value,
                                )
                              }
                              displayEmpty
                              variant="standard"
                              size="small"
                              disableUnderline
                              renderValue={(val) => {
                                const config = priorityConfig[val as string];
                                return config ? (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      cursor: loading
                                        ? "not-allowed"
                                        : "pointer",
                                      pointerEvents: loading ? "none" : "auto",
                                    }}
                                  >
                                    {config.icon}
                                  </Box>
                                ) : (
                                  <FormatListBulletedIcon
                                    fontSize="medium"
                                    sx={{
                                      color: loading
                                        ? colors.GrayDarkMode[300].$value
                                        : colors.GrayDarkMode[700].$value,
                                      cursor: loading
                                        ? "not-allowed"
                                        : "pointer",
                                      pointerEvents: loading ? "none" : "auto",
                                    }}
                                  />
                                );
                              }}
                              sx={{
                                fontSize: typography.Size.Text.sm.$value,
                                minWidth: "unset",
                                "& .MuiSelect-select": {
                                  paddingRight:
                                    spacing.Spacing.md.$value + " !important",
                                  paddingBottom:
                                    spacing.Spacing.none.$value + " !important",
                                  paddingTop:
                                    spacing.Spacing.none.$value + " !important",
                                  display: "flex",
                                  alignItems: "center",
                                },
                                "& .MuiSelect-icon": { position: "relative" },
                              }}
                            >
                              {Object.entries({
                                high: {
                                  label: "High",
                                  icon: (
                                    <ExpandLessIcon
                                      fontSize="medium"
                                      sx={{ color: colors.Error[500].$value }}
                                    />
                                  ),
                                },
                                medium: {
                                  label: "Medium",
                                  icon: (
                                    <DragHandleIcon
                                      fontSize="medium"
                                      sx={{ color: colors.Warning[500].$value }}
                                    />
                                  ),
                                },
                                low: {
                                  label: "Low",
                                  icon: (
                                    <ExpandMoreIcon
                                      fontSize="medium"
                                      sx={{ color: colors.Blue[500].$value }}
                                    />
                                  ),
                                },
                                highest: {
                                  label: "Highest",
                                  icon: (
                                    <KeyboardDoubleArrowUpIcon
                                      fontSize="medium"
                                      sx={{ color: colors.Error[500].$value }}
                                    />
                                  ),
                                },
                                lowest: {
                                  label: "Lowest",
                                  icon: (
                                    <KeyboardDoubleArrowDownIcon
                                      fontSize="medium"
                                      sx={{ color: colors.Blue[500].$value }}
                                    />
                                  ),
                                },
                              }).map(([value, { label, icon }]) => (
                                <MenuItem key={value} value={value}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: spacing.Spacing.md.$value,
                                    }}
                                  >
                                    {icon}
                                    {label}
                                  </Box>
                                </MenuItem>
                              ))}
                            </Select>
                            {formError[task.itemId]?.priority && (
                              <FormHelperText>
                                {formError[task.itemId].priority}
                              </FormHelperText>
                            )}
                          </FormControl>
                        );
                      })()}

                    {/* Assignee — hide if shared */}
                    {!sharedProps?.assignee?.id && (
                      <Select
                        disabled={loading}
                        value={task.assignee}
                        onChange={(e) =>
                          updateTask(task.itemId, "assignee", e.target.value)
                        }
                        displayEmpty
                        variant="standard"
                        size="small"
                        disableUnderline
                        renderValue={(val) => {
                          const selectedUser = allUsers?.find(
                            (u: any) => u?.id === val,
                          );
                          return selectedUser ? (
                            <Avatar
                              src={selectedUser?.avatarUrl}
                              sx={{
                                width: 24,
                                height: 24,
                                fontSize: 12,
                                cursor: loading ? "not-allowed" : "pointer",
                                opacity: loading ? 0.4 : 1,
                              }}
                            >
                              {selectedUser?.first_name
                                ?.charAt(0)
                                ?.toUpperCase() ?? "?"}
                            </Avatar>
                          ) : (
                            <AccountCircleIcon
                              fontSize="medium"
                              sx={{
                                color: loading
                                  ? colors.GrayDarkMode[300].$value
                                  : colors.GrayDarkMode[700].$value,
                                cursor: loading ? "not-allowed" : "pointer",
                                display: "flex",
                              }}
                            />
                          );
                        }}
                        sx={{
                          fontSize: typography.Size.Text.sm.$value,
                          minWidth: "unset",
                          "& .MuiSelect-select": {
                            paddingRight:
                              spacing.Spacing.md.$value + " !important",
                            paddingBottom:
                              spacing.Spacing.none.$value + " !important",
                            paddingTop:
                              spacing.Spacing.none.$value + " !important",
                            display: "flex",
                            alignItems: "center",
                          },
                          "& .MuiSelect-icon": { position: "relative" },
                        }}
                      >
                        {/* <MenuItem value="">Select Assignee</MenuItem> */}
                        {allUsers?.map((user: any) => (
                          <MenuItem key={user.id} value={user.id}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Avatar
                                src={user?.avatarUrl}
                                sx={{ width: 24, height: 24, fontSize: 12 }}
                              >
                                {user?.first_name?.charAt(0)?.toUpperCase() ??
                                  "?"}
                              </Avatar>
                              {`${user?.first_name ?? ""} ${user?.last_name ?? ""}`.trim()}
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          ))}

          {/* Add Another */}
          <Button
            disabled={loading}
            startIcon={<AddIcon />}
            onClick={addAnother}
            sx={{
              color: loading
                ? colors.GrayDarkMode[300].$value
                : colors.Brand[600].$value,
              textTransform: "none",
              fontWeight: typography.Font_weight.Medium.$value,
              mb: spacing.Spacing["3xl"].$value,
              width: "100%",
              justifyContent: "center",
            }}
          >
            ADD ANOTHER
          </Button>
        </Box>
      </Box>
    </LocalizationProvider>
  );
}
